import React from 'react';
import { Link } from 'react-router-dom';
import { BsKanban, BsClockHistory, BsReceipt, BsKey, BsGlobe, BsHddNetwork, BsCreditCard2Back, BsFileText } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import { formatEur, formatDate } from '../../utils/api';
import StatusBadge from './components/StatusBadge';
import SkeletonLoader from './components/SkeletonLoader';

// Estados de factura que cuentan como "pendiente de pago"
const PENDING_INVOICE_STATES = ['Pendiente', 'Parcial', 'Validada'];

const CustomerDashboardPage = () => {
  const { customer, licenses, invoices, domains, hostings, projects, timePacks, loading, error } = useCustomer();

  if (loading) {
    return (
      <div>
        <div className="cp-skeleton cp-skeleton-title" />
        <SkeletonLoader type="stats" count={4} />
        <div className="cp-two-col">
          <div className="cp-skeleton cp-skeleton-card" style={{ height: 200 }} />
          <div className="cp-skeleton cp-skeleton-card" style={{ height: 200 }} />
        </div>
      </div>
    );
  }

  // --- Derivados de valor para el cliente de agencia ---
  const activeProjects = (projects || []).filter((p) => p.estado === 'en_proceso');

  // Bono de horas activo (lo más consultado): saldo + % consumido
  const activePack = (timePacks || []).find((p) => p.status === 'activa') || null;
  let packView = null;
  if (activePack) {
    const totalMin = (Number(activePack.total_hours) || 0) * 60;
    const consumido = Number(activePack.minutos_consumidos) || 0;
    const restante = totalMin - consumido;
    const pct = totalMin > 0 ? Math.min(100, (consumido / totalMin) * 100) : 0;
    const over = totalMin > 0 && consumido > totalMin;
    packView = {
      ref: activePack.referencia,
      label: activePack.label,
      totalHours: Number(activePack.total_hours) || 0,
      restanteHoras: restante / 60,
      pct,
      over
    };
  }

  // Facturas pendientes de pago (importe + nº). Las proformas no son facturas
  // fiscales: quedan fuera del importe pendiente (mismo criterio que /cliente/facturas).
  const pendingInvoices = (invoices || []).filter(
    (i) => i.invoice_type !== 'proforma' && PENDING_INVOICE_STATES.includes(i.estado)
  );
  const pendingTotal = pendingInvoices.reduce((acc, i) => acc + (Number(i.total) || 0), 0);

  // Próxima renovación de infraestructura (dominio u hosting)
  const infraItems = [
    ...(domains || []).map((d) => ({ nombre: d.dominio, renueva: d.fecha_renovacion })),
    ...(hostings || []).map((h) => ({ nombre: h.hosting, renueva: h.fecha_renovacion }))
  ].filter((x) => x.renueva);
  const nextRenewal = infraItems
    .slice()
    .sort((a, b) => new Date(a.renueva) - new Date(b.renueva))[0] || null;

  // Estilo de barra de progreso del bono (degradado de marca / rojo si over)
  const progressFill = packView && packView.over
    ? '#dc3545'
    : 'linear-gradient(90deg,#50BAFC,#2E9BE0)';

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid var(--cp-border)',
    fontSize: 14
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="cp-page-title" style={{ marginBottom: 4 }}>Hola, {customer?.name || 'Cliente'}</h1>
        <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: 0 }}>{new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {error && <div className="cp-alert cp-alert-error">{error}</div>}

      {/* Stat cards: priorizan lo de valor (proyectos, bono, facturas pendientes) */}
      <div className="cp-stats-grid">
        <div className="cp-stat-card blue">
          <div className="cp-stat-icon"><BsKanban /></div>
          <div className="cp-stat-label">Proyectos en curso</div>
          <div className="cp-stat-value">{activeProjects.length}</div>
        </div>

        <div className="cp-stat-card green">
          <div className="cp-stat-icon"><BsClockHistory /></div>
          <div className="cp-stat-label">Bono de horas</div>
          {packView ? (
            <>
              <div className="cp-stat-value">
                {packView.restanteHoras.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h
              </div>
              <div style={{ fontSize: 12, color: 'var(--cp-muted)', marginTop: 2 }}>restantes de {packView.totalHours} h</div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--cp-muted)', marginTop: 8 }}>Sin bono activo</div>
          )}
        </div>

        <div className="cp-stat-card orange">
          <div className="cp-stat-icon"><BsReceipt /></div>
          <div className="cp-stat-label">Facturas pendientes</div>
          <div className="cp-stat-value">{pendingInvoices.length}</div>
          {pendingInvoices.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--cp-muted)', marginTop: 2 }}>{formatEur(pendingTotal)}</div>
          )}
        </div>

        {/* Licencias: solo si el cliente realmente tiene licencias */}
        {licenses.length > 0 ? (
          <div className="cp-stat-card purple">
            <div className="cp-stat-icon"><BsKey /></div>
            <div className="cp-stat-label">Licencias</div>
            <div className="cp-stat-value">{licenses.length}</div>
          </div>
        ) : (
          <div className="cp-stat-card purple">
            <div className="cp-stat-icon"><BsGlobe /></div>
            <div className="cp-stat-label">Infraestructura</div>
            <div className="cp-stat-value">{domains.length + hostings.length}</div>
            {nextRenewal ? (
              <div style={{ fontSize: 12, color: 'var(--cp-muted)', marginTop: 2 }}>Renueva: {formatDate(nextRenewal.renueva)}</div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--cp-muted)', marginTop: 2 }}>{domains.length} dominios · {hostings.length} hostings</div>
            )}
          </div>
        )}
      </div>

      <div className="cp-actions-grid">
        <Link to="/cliente/proyectos" className="cp-action-card">
          <div className="cp-action-icon blue"><BsKanban /></div>
          <span>Ver proyectos</span>
        </Link>
        <Link to="/cliente/bolsas-de-horas" className="cp-action-card">
          <div className="cp-action-icon green"><BsClockHistory /></div>
          <span>Bono de horas</span>
        </Link>
        <Link to="/cliente/facturas" className="cp-action-card">
          <div className="cp-action-icon orange"><BsReceipt /></div>
          <span>Ver facturas</span>
        </Link>
        {licenses.length > 0 && (
          <Link to="/cliente/licencias" className="cp-action-card">
            <div className="cp-action-icon purple"><BsCreditCard2Back /></div>
            <span>Gestionar licencias</span>
          </Link>
        )}
      </div>

      {/* Panel destacado: bono de horas activo con barra de progreso */}
      {packView && (
        <div className="cp-card-static" style={{ marginBottom: 16 }}>
          <div className="cp-card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: 'var(--cp-text)' }}>
                Bono de horas activo
                {packView.ref && (
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--cp-muted)', marginLeft: 8 }}>{packView.ref}</span>
                )}
              </h3>
              <span style={{ fontSize: 13, color: packView.over ? '#dc3545' : 'var(--cp-muted)' }}>
                {packView.pct.toLocaleString('es-ES', { maximumFractionDigits: 0 })}% consumido
              </span>
            </div>
            {packView.label && (
              <div style={{ fontSize: 13, color: 'var(--cp-muted)', marginBottom: 10 }}>{packView.label}</div>
            )}
            <div style={{ height: 10, borderRadius: 999, background: 'var(--cp-soft, #E8F5FF)', overflow: 'hidden' }}>
              <div style={{ width: `${packView.pct}%`, height: '100%', background: progressFill, borderRadius: 999, transition: 'width .3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
              <span style={{ color: packView.over ? '#dc3545' : 'var(--cp-text)', fontWeight: 600 }}>
                {packView.restanteHoras.toLocaleString('es-ES', { maximumFractionDigits: 1 })} h restantes
              </span>
              <span style={{ color: 'var(--cp-muted)' }}>de {packView.totalHours} h</span>
            </div>
          </div>
        </div>
      )}

      <div className="cp-two-col">
        {/* Facturas pendientes de pago */}
        <div className="cp-card-static">
          <div className="cp-card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: 'var(--cp-text)' }}>Facturas pendientes de pago</h3>
              {pendingInvoices.length > 0 && (
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--cp-text)' }}>{formatEur(pendingTotal)}</span>
              )}
            </div>
            {!pendingInvoices.length ? (
              <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: 0 }}>No tienes facturas pendientes de pago.</p>
            ) : (
              pendingInvoices.slice(0, 5).map((i) => (
                <div key={i.id} style={rowStyle}>
                  <div>
                    <span style={{ fontWeight: 500, color: 'var(--cp-text)' }}>{i.numero || `#${i.id}`}</span>
                    <div style={{ fontSize: 12, color: 'var(--cp-muted)' }}>{formatDate(i.fecha)}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: 'var(--cp-text)', fontWeight: 500 }}>{formatEur(i.total)}</span>
                    <StatusBadge status={i.estado} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Proyectos en curso */}
        <div className="cp-card-static">
          <div className="cp-card-body">
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--cp-text)' }}>Proyectos en curso</h3>
            {!activeProjects.length ? (
              <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: 0 }}>No hay proyectos en curso ahora mismo.</p>
            ) : (
              activeProjects.slice(0, 5).map((p) => (
                <div key={p.id} style={rowStyle}>
                  <div>
                    <span style={{ fontWeight: 500, color: 'var(--cp-text)' }}>{p.titulo || `#${p.id}`}</span>
                    {p.fecha_inicio && (
                      <div style={{ fontSize: 12, color: 'var(--cp-muted)' }}>Desde {formatDate(p.fecha_inicio)}</div>
                    )}
                  </div>
                  <StatusBadge status={p.estado} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Últimas facturas (importe con formatEur) */}
      <div className="cp-card-static" style={{ marginTop: 16 }}>
        <div className="cp-card-body">
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--cp-text)' }}>Últimas facturas</h3>
          {!invoices.length ? (
            <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: 0 }}>Sin facturas</p>
          ) : (
            invoices.slice(0, 5).map((i) => (
              <div key={i.id} style={rowStyle}>
                <span style={{ fontWeight: 500, color: 'var(--cp-text)' }}>{i.numero || `#${i.id}`}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'var(--cp-text)' }}>{formatEur(i.total)}</span>
                  <StatusBadge status={i.estado} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Infraestructura: dominios y hostings separados (sin número ambiguo) */}
      {(domains.length > 0 || hostings.length > 0) && (
        <div className="cp-two-col" style={{ marginTop: 16 }}>
          <div className="cp-card-static">
            <div className="cp-card-body">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--cp-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <BsGlobe style={{ color: '#2E9BE0' }} /> Dominios <span style={{ color: 'var(--cp-muted)', fontWeight: 500 }}>({domains.length})</span>
              </h3>
              {!domains.length ? (
                <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: 0 }}>Sin dominios</p>
              ) : (
                domains.slice(0, 5).map((d) => (
                  <div key={d.id} style={rowStyle}>
                    <span style={{ fontWeight: 500, color: 'var(--cp-text)' }}>{d.dominio}</span>
                    {d.fecha_renovacion && (
                      <span style={{ fontSize: 12, color: 'var(--cp-muted)' }}>Renueva: {formatDate(d.fecha_renovacion)}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="cp-card-static">
            <div className="cp-card-body">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--cp-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <BsHddNetwork style={{ color: '#2E9BE0' }} /> Hostings <span style={{ color: 'var(--cp-muted)', fontWeight: 500 }}>({hostings.length})</span>
              </h3>
              {!hostings.length ? (
                <p style={{ color: 'var(--cp-muted)', fontSize: 14, margin: 0 }}>Sin hostings</p>
              ) : (
                hostings.slice(0, 5).map((h) => (
                  <div key={h.id} style={rowStyle}>
                    <span style={{ fontWeight: 500, color: 'var(--cp-text)' }}>{h.hosting}</span>
                    {h.fecha_renovacion && (
                      <span style={{ fontSize: 12, color: 'var(--cp-muted)' }}>Renueva: {formatDate(h.fecha_renovacion)}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Licencias activas: solo si existen */}
      {licenses.length > 0 && (
        <div className="cp-card-static" style={{ marginTop: 16 }}>
          <div className="cp-card-body">
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--cp-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <BsFileText style={{ color: '#2E9BE0' }} /> Licencias activas
            </h3>
            {licenses.slice(0, 5).map((l) => (
              <div key={l.id} style={rowStyle}>
                <div>
                  <span style={{ fontWeight: 500, color: 'var(--cp-text)' }}>{l.product_name}</span>
                  <div style={{ fontSize: 12, color: 'var(--cp-muted)' }}>Expira: {formatDate(l.expires_at)}</div>
                </div>
                <StatusBadge status={l.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboardPage;
